import React, { useState, useEffect } from 'react';
import { X, Loader2, Wand2, Send, RefreshCw, Mail, User, Building2, Zap, Edit3, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { fetchWithAuth, API_URL } from './constants';
import { toast } from 'sonner';

const TONES = ['professional', 'conversational', 'direct', 'consultative'];

export const QuickOutreachModal = ({ company, onClose, onSent }) => {
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tone, setTone] = useState('professional');
  const [steps, setSteps] = useState(3);
  const [sending, setSending] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [expandedStep, setExpandedStep] = useState(0);

  const generate = async () => {
    setGenerating(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/ai/personalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company.company,
          num_steps: steps,
          tone: tone,
          ab_testing: false,
        }),
      });
      if (data.error) {
        toast.error(data.detail || 'Generation failed');
      } else {
        setPreview(data);
        setExpandedStep(0);
      }
    } catch (err) {
      toast.error('Failed to generate outreach sequence');
    }
    setGenerating(false);
  };

  useEffect(() => { generate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveEdit = (stepIdx) => {
    if (!preview) return;
    const updated = { ...preview };
    const seq = updated.sequences[0];
    if (seq?.steps?.[stepIdx]?.variants?.[0]) {
      seq.steps[stepIdx].variants[0].subject = editSubject;
      seq.steps[stepIdx].variants[0].body = editBody;
    }
    setPreview(updated);
    setEditingStep(null);
  };

  const handleSendToQueue = async () => {
    setSending(true);
    try {
      // 1. Save the sequence as a template
      const saveRes = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/ai/personalize-and-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company.company,
          num_steps: steps,
          tone: tone,
          ab_testing: false,
        }),
      });

      if (!saveRes.success) {
        toast.error(saveRes.detail || 'Failed to save sequence');
        setSending(false);
        return;
      }

      // 2. Trigger autopilot sync to update status
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/autopilot/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company.company,
          event_type: 'email_sent',
          details: `Quick Outreach: ${steps}-step ${tone} sequence queued via Start Here`,
        }),
      });

      toast.success(`Outreach queued for ${company.company} — status updated`);
      onSent(company.company);
      onClose();
    } catch (err) {
      toast.error('Failed to queue outreach');
    }
    setSending(false);
  };

  const allSteps = preview?.sequences?.[0]?.steps || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="quick-outreach-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-lg flex items-center justify-center">
              <Mail className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Quick Outreach</h2>
              <p className="text-[10px] text-gray-500">{company.company}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-quick-outreach">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Company context bar */}
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-4 flex-wrap shrink-0">
          <span className="text-[10px] text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" />{company.industry || 'Unknown industry'}</span>
          <span className="text-[10px] text-gray-500 flex items-center gap-1"><User className="h-3 w-3" />{company.employee_count ? `${company.employee_count.toLocaleString()} emp` : 'Size unknown'}</span>
          <span className="text-[10px] text-gray-500 flex items-center gap-1"><Zap className="h-3 w-3" />Activity: {company.auto_score}</span>
          {company.why_now?.length > 0 && (
            <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{company.why_now[0]}</span>
          )}
          {company.decision_makers_count > 0 && (
            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{company.decision_makers_count} contact{company.decision_makers_count > 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0">
          <select value={tone} onChange={e => setTone(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
            data-testid="outreach-tone">
            {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <select value={steps} onChange={e => setSteps(Number(e.target.value))}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
            data-testid="outreach-steps">
            <option value={2}>2 steps</option>
            <option value={3}>3 steps</option>
            <option value={4}>4 steps</option>
            <option value={5}>5 steps</option>
          </select>
          <button onClick={generate} disabled={generating}
            className="px-3 py-1.5 text-xs font-semibold text-[#0A1F44] bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1 disabled:opacity-50"
            data-testid="regenerate-btn">
            {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Regenerate
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {generating && !preview ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37] mb-3" />
              <p className="text-sm text-gray-500">Generating personalized sequence...</p>
              <p className="text-[10px] text-gray-400 mt-1">Using company context, hiring signals, and industry data</p>
            </div>
          ) : preview ? (
            <>
              {/* Preview header */}
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#0A1F44] text-[10px] font-medium rounded flex items-center gap-1"><Wand2 className="h-3 w-3" /> AI-Generated</span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{allSteps.length} steps</span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{preview.total_days} days</span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded capitalize">{tone}</span>
              </div>

              {/* Steps */}
              {allSteps.map((step, si) => {
                const v = step.variants?.[0] || {};
                const isExpanded = expandedStep === si;
                const isEditing = editingStep === si;

                return (
                  <div key={si} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`outreach-step-${si}`}>
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedStep(isExpanded ? null : si)}>
                      <div className="h-6 w-6 bg-[#0A1F44] rounded-full flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-[#D4AF37]">{si + 1}</span>
                      </div>
                      <span className="text-xs font-medium text-[#0A1F44] flex-1">Step {si + 1}</span>
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] rounded">{step.delay} {step.delay_unit}</span>
                      {!isEditing && (
                        <button onClick={(e) => { e.stopPropagation(); setEditingStep(si); setEditSubject(v.subject || ''); setEditBody(v.body || ''); setExpandedStep(si); }}
                          className="p-1 hover:bg-gray-200 rounded" data-testid={`edit-step-${si}`}>
                          <Edit3 className="h-3 w-3 text-gray-400" />
                        </button>
                      )}
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                    </div>

                    {isExpanded && (
                      <div className="p-4 space-y-2">
                        {isEditing ? (
                          <>
                            <div>
                              <p className="text-[10px] text-gray-400 mb-1">Subject</p>
                              <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30"
                                data-testid={`edit-subject-${si}`} />
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 mb-1">Body</p>
                              <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={6}
                                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/30 resize-none"
                                data-testid={`edit-body-${si}`} />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveEdit(si)}
                                className="px-3 py-1.5 text-[10px] font-semibold bg-[#0A1F44] text-white rounded-lg flex items-center gap-1"
                                data-testid={`save-edit-${si}`}>
                                <Check className="h-3 w-3" /> Save Edit
                              </button>
                              <button onClick={() => setEditingStep(null)}
                                className="px-3 py-1.5 text-[10px] text-gray-500 hover:text-gray-700">Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-[10px] text-gray-400">Subject</p>
                              <p className="text-xs font-medium text-[#0A1F44]">{v.subject}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400">Body</p>
                              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{v.body}</div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Click Regenerate to create a sequence</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <p className="text-[10px] text-gray-400">
            {preview ? `${allSteps.length}-step sequence ready for review` : 'Generate a sequence first'}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleSendToQueue} disabled={!preview || sending || generating}
              className="px-5 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
              data-testid="send-to-queue-btn">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Save & Queue Outreach
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
